import React from 'react';
import { Shield, Clock, Layers, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { parseDateOnly } from '@/lib/dateOnly';
import type { MonteCarloResult } from '@/hooks/useEmpMonteCarlo';
import type { McProjectionState } from '@/hooks/useMcProjectionState';

interface Props {
  mcLatest: MonteCarloResult | null;
  mcState: McProjectionState;
}

const fmtTS = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    return format(new Date(iso), "dd/MM HH:mm");
  } catch { return '—'; }
};

const sourceLabels: Record<string, string> = {
  manual: 'Manual',
  auto_queue: 'Automático',
  boletim_aprovado: 'Boletim aprovado',
  boletim_aprovado_para_faturamento: 'Boletim aprovado',
  boletim_rejeitado: 'Boletim rejeitado',
  baseline_ativada: 'Nova baseline',
  contrato_data_fim: 'Alteração de prazo',
  aditivo_aprovado: 'Aditivo',
  cronograma_aprovado: 'Cronograma',
  planilha_ativada: 'Planilha ativada',
  validacao_insert: 'Nova execução',
  validacao_update: 'Alteração execução',
  curva_s_snapshot: 'Snapshot curva S',
  fix_c1_c5: 'Correção C1-C5',
};

export const McConfidenceFooter: React.FC<Props> = ({ mcLatest, mcState }) => {
  if (!mcLatest) return null;

  const source = mcLatest.triggered_by
    ? sourceLabels[mcLatest.triggered_by] || mcLatest.triggered_by
    : '—';

  return (
    <div className={cn(
      'flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 rounded-md text-[10px] font-medium tracking-wide uppercase',
      'border border-border/50',
      mcState === 'fresh' && 'bg-success/5 text-muted-foreground',
      mcState === 'stale' && 'bg-warning/5 text-warning',
      mcState === 'error' && 'bg-destructive/5 text-destructive',
    )}>
      <span className="flex items-center gap-1">
        <Shield className="h-3 w-3" />
        Simulação v{mcLatest.versao_logica}
      </span>
      <span className="flex items-center gap-1">
        <Clock className="h-3 w-3" />
        {fmtTS(mcLatest.created_at)}
      </span>
      <span className="flex items-center gap-1">
        <Layers className="h-3 w-3" />
        {mcLatest.n_iteracoes.toLocaleString()} iterações
      </span>
      <span className="flex items-center gap-1">
        <Zap className="h-3 w-3" />
        Origem: {source}
      </span>
      <span className={cn(
        'ml-auto flex items-center gap-1',
        mcState === 'fresh' && 'text-success',
        mcState === 'stale' && 'text-warning',
        mcState === 'error' && 'text-destructive',
      )}>
        <span className={cn(
          'w-1.5 h-1.5 rounded-full',
          mcState === 'fresh' && 'bg-success',
          mcState === 'stale' && 'bg-warning animate-pulse',
          mcState === 'error' && 'bg-destructive',
        )} />
        {mcState === 'fresh' && 'Atualizada'}
        {mcState === 'stale' && 'Atualizando...'}
        {mcState === 'error' && 'Erro'}
      </span>
    </div>
  );
};
